import { useState } from 'react';
import { useApi, fmtKST, fmtNum } from '../hooks/useApi';
import SyncFreshnessBadge from './SyncFreshnessBadge';

export default function TradeHistory() {
  const [view, setView] = useState('trades'); // trades | orders
  const { data: trades, loading: tLoading } = useApi('/api/trades/history?limit=100');
  const { data: orders, loading: oLoading } = useApi('/api/trades/orders?limit=100');
  const { data: stats } = useApi('/api/trades/stats');

  return (
    <div className="space-y-4">
      {/* 거래 통계 */}
      {stats && (
        <div className="flex justify-end">
          <SyncFreshnessBadge lastSyncedAt={stats.last_synced_at} />
        </div>
      )}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: '총 거래', value: stats.total_trades },
            { label: '총 거래대금', value: '₩' + fmt(stats.total_volume) },
            { label: '실현 손익', value: (stats.total_realized_pnl >= 0 ? '+' : '') + '₩' + fmt(stats.total_realized_pnl),
              color: stats.total_realized_pnl >= 0 ? 'text-green-400' : 'text-red-400' },
            { label: '매수', value: stats.buy_count + '건' },
            { label: '매도', value: stats.sell_count + '건' },
          ].map((s) => (
            <div key={s.label} className="bg-gray-800 rounded-lg p-3 border border-gray-700">
              <div className="text-xs text-gray-500">{s.label}</div>
              <div className={`text-sm font-semibold ${s.color || ''}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* 탭 */}
      <div className="flex gap-2">
        <button
          onClick={() => setView('trades')}
          className={`px-3 py-2 text-sm rounded min-h-[44px] ${view === 'trades' ? 'bg-blue-600' : 'bg-gray-700'}`}
        >체결 거래</button>
        <button
          onClick={() => setView('orders')}
          className={`px-3 py-2 text-sm rounded min-h-[44px] ${view === 'orders' ? 'bg-blue-600' : 'bg-gray-700'}`}
        >주문 이력</button>
      </div>

      {/* 테이블 */}
      <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-x-auto">
        {view === 'trades' ? (
          <TradesTable data={trades} loading={tLoading} />
        ) : (
          <OrdersTable data={orders} loading={oLoading} />
        )}
      </div>
    </div>
  );
}

function TradesTable({ data, loading }) {
  if (loading) return <div className="p-4 text-sm text-gray-500">로딩 중...</div>;
  if (!data || data.length === 0) return <div className="p-4 text-sm text-gray-500">거래 기록 없음</div>;

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-500 border-b border-gray-700">
          <th className="text-left p-2">봇</th>
          <th className="text-left p-2">종목</th>
          <th className="text-center p-2">구분</th>
          <th className="text-right p-2">수량</th>
          <th className="text-right p-2">매입가</th>
          <th className="text-right p-2">현재가</th>
          <th className="text-right p-2 hidden sm:table-cell">목표가</th>
          <th className="text-right p-2 hidden sm:table-cell">손절가</th>
          <th className="text-right p-2">손익</th>
          <th className="text-right p-2">수익률</th>
          <th className="text-center p-2 hidden md:table-cell">시장</th>
          <th className="text-right p-2 hidden lg:table-cell">시각</th>
        </tr>
      </thead>
      <tbody>
        {data.map((t, i) => {
          const c = t.market === 'US' ? '$' : '₩';
          const pnl = t.realized_pnl != null ? t.realized_pnl : t.unrealized_pnl;
          const pnlPct = t.realized_pnl_pct != null ? t.realized_pnl_pct : t.unrealized_pnl_pct;
          const isRealized = t.realized_pnl != null;
          return (
          <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-750">
            <td className="p-2">{t.bot_name}</td>
            <td className="p-2">
              <span className="font-medium">{t.ticker}</span>
              <span className="text-gray-500 ml-1">{t.stock_name}</span>
            </td>
            <td className="text-center p-2">
              <span className={`px-1.5 py-0.5 rounded text-xs ${
                t.trade_type === 'BUY' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
              }`}>{t.trade_type}</span>
            </td>
            <td className="text-right p-2">{t.quantity}</td>
            <td className="text-right p-2">{c}{fmt(t.price)}</td>
            <td className="text-right p-2">{t.current_price ? `${c}${fmt(t.current_price)}` : '-'}</td>
            <td className="text-right p-2 text-blue-400 hidden sm:table-cell">{t.target_price ? `${c}${fmt(t.target_price)}` : '-'}</td>
            <td className="text-right p-2 text-orange-400 hidden sm:table-cell">{t.stop_loss ? `${c}${fmt(t.stop_loss)}` : '-'}</td>
            <td className={`text-right p-2 ${
              pnl != null && pnl >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {pnl != null ? <>{c}{fmt(pnl)}{!isRealized && <span className="text-gray-600 text-[10px] ml-0.5">평가</span>}</> : '-'}
            </td>
            <td className={`text-right p-2 ${
              pnlPct != null && pnlPct >= 0 ? 'text-green-400' : 'text-red-400'
            }`}>
              {pnlPct != null ? `${pnlPct >= 0 ? '+' : ''}${pnlPct.toFixed(2)}%` : '-'}
            </td>
            <td className="text-center p-2 hidden md:table-cell">
              <span className={`px-1 py-0.5 rounded text-xs ${
                t.market === 'US' ? 'bg-blue-900 text-blue-300' : 'bg-gray-700 text-gray-300'
              }`}>{t.market}</span>
            </td>
            <td className="text-right p-2 text-gray-500 hidden lg:table-cell">{fmtDate(t.traded_at)}</td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function OrdersTable({ data, loading }) {
  if (loading) return <div className="p-4 text-sm text-gray-500">로딩 중...</div>;
  if (!data || data.length === 0) return <div className="p-4 text-sm text-gray-500">주문 기록 없음</div>;

  const STATUS_COLORS = {
    FILLED: 'bg-green-900 text-green-300',
    REJECTED: 'bg-red-900 text-red-300',
    PENDING: 'bg-yellow-900 text-yellow-300',
    CANCELLED: 'bg-gray-700 text-gray-400',
  };

  return (
    <table className="w-full text-xs">
      <thead>
        <tr className="text-gray-500 border-b border-gray-700">
          <th className="text-left p-2">봇</th>
          <th className="text-left p-2">종목</th>
          <th className="text-center p-2">구분</th>
          <th className="text-right p-2">수량</th>
          <th className="text-right p-2">요청가</th>
          <th className="text-right p-2 hidden sm:table-cell">체결가</th>
          <th className="text-right p-2 hidden sm:table-cell">목표가</th>
          <th className="text-right p-2 hidden sm:table-cell">손절가</th>
          <th className="text-center p-2">상태</th>
          <th className="text-right p-2">확신도</th>
          <th className="text-right p-2 hidden lg:table-cell">시각</th>
        </tr>
      </thead>
      <tbody>
        {data.map((o, i) => {
          const c = o.market === 'US' ? '$' : '₩';
          return (
          <tr key={i} className="border-b border-gray-700/50 hover:bg-gray-750">
            <td className="p-2">{o.bot_name}</td>
            <td className="p-2">
              <span className="font-medium">{o.ticker}</span>
              <span className="text-gray-500 ml-1">{o.stock_name}</span>
            </td>
            <td className="text-center p-2">
              <span className={`px-1.5 py-0.5 rounded text-xs ${
                o.order_type === 'BUY' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'
              }`}>{o.order_type}</span>
            </td>
            <td className="text-right p-2">{o.quantity}</td>
            <td className="text-right p-2">{c}{fmt(o.requested_price)}</td>
            <td className="text-right p-2 hidden sm:table-cell">{o.executed_price ? `${c}${fmt(o.executed_price)}` : '-'}</td>
            <td className="text-right p-2 text-blue-400 hidden sm:table-cell">{o.target_price ? `${c}${fmt(o.target_price)}` : '-'}</td>
            <td className="text-right p-2 text-orange-400 hidden sm:table-cell">{o.stop_loss ? `${c}${fmt(o.stop_loss)}` : '-'}</td>
            <td className="text-center p-2">
              <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_COLORS[o.status] || 'bg-gray-700'}`}>
                {o.status}
              </span>
            </td>
            <td className="text-right p-2">{Number(o.confidence).toFixed(0)}%</td>
            <td className="text-right p-2 text-gray-500 hidden lg:table-cell">{fmtDate(o.requested_at)}</td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

const fmt = fmtNum;
const fmtDate = fmtKST;
